import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const DispatchEntryForm = () => {
  const [formData, setFormData] = useState({
    sales_order_id: '',
    do_id: '',
    dispatch_date: '',
    weight_slip_ref_no: '',
    vehicle_no: '',
    lifted_quantity_mt: '',
    warehouse_id: '',
    commodity_id: '',
    dispatch_amount: '',
    invoice_no: '',
    invoice_date: '',
    invoice_amount: '',
    cha_cma_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [cmas, setCmas] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [saleOrders, setSaleOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const navigate = useNavigate();

  // Fetch CMA and Commodity dropdowns on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch CMAs
        const cmaRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, {
          credentials: 'include'
        });
        if (cmaRes.ok) {
          const cmaData = await cmaRes.json();
          setCmas(cmaData.data || []);
        }
        // Fetch Commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        }
        // Fetch Sale Orders
        const saleOrderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-sale-order`, {
          credentials: 'include'
        });
        if (saleOrderRes.ok) {
          const saleOrderData = await saleOrderRes.json();
          setSaleOrders(saleOrderData.data || []);
        }
        // Fetch Delivery Orders
        const deliveryOrderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-do`, {
          credentials: 'include'
        });
        if (deliveryOrderRes.ok) {
          const deliveryOrderData = await deliveryOrderRes.json();
          setDeliveryOrders(deliveryOrderData.data || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch warehouses by CMA
  const fetchWarehousesByCma = async (cmaId) => {
    if (!cmaId) {
      setWarehouses([]);
      return;
    }
    try {
      const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cha_cma_id=${cmaId}`, {
        credentials: 'include'
      });
      if (warehouseRes.ok) {
        const warehouseData = await warehouseRes.json();
        setWarehouses(warehouseData.data || []);
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      setWarehouses([]);
    }
  };

  const getMaxDate = () => new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  const preventDateTyping = (e) => {
    if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'dispatch_date' && value) {
      const maxDate = getMaxDate();
      if (value > maxDate) {
        setMessage({ type: 'error', text: 'Future dates are not allowed for Dispatch Date.' });
        return;
      }
    }
    if (name === 'dispatch_date') {
      setMessage(prev => (prev?.type === 'error' && prev?.text === 'Future dates are not allowed for Dispatch Date.' ? null : prev));
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'cha_cma_id') {
      fetchWarehousesByCma(value);
      setFormData(prev => ({ ...prev, warehouse_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const maxDate = getMaxDate();
    if (formData.dispatch_date && formData.dispatch_date > maxDate) {
      setMessage({ type: 'error', text: 'Future dates are not allowed for Dispatch Date.' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/dispatch-entry/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: '✅ Dispatch Entry saved successfully!' });
        setFormData({
          sales_order_id: '',
          do_id: '',
          dispatch_date: '',
          weight_slip_ref_no: '',
          vehicle_no: '',
          lifted_quantity_mt: '',
          warehouse_id: '',
          commodity_id: '',
          dispatch_amount: '',
          invoice_no: '',
          invoice_date: '',
          invoice_amount: '',
          cha_cma_id: ''
        });
        setWarehouses([]);
      } else {
        setMessage({ type: 'error', text: data.error || '❌ Error saving dispatch entry.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Error saving dispatch entry.' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Dispatch Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Sales Order (Buyer Name)</label>
              <SearchableDropdown
                options={saleOrders}
                value={formData.sales_order_id}
                onChange={val => handleDropdownChange('sales_order_id', val)}
                placeholder="Select Sales Order"
                displayKey="buyer_name"
                valueKey="sale_id"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Delivery Order No</label>
              <SearchableDropdown
                options={deliveryOrders}
                value={formData.do_id}
                onChange={val => handleDropdownChange('do_id', val)}
                placeholder="Select Delivery Order"
                displayKey="do_number"
                valueKey="do_id"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Dispatch Date</label>
              <input
                type="date"
                name="dispatch_date"
                value={formData.dispatch_date}
                onChange={handleChange}
                onKeyDown={preventDateTyping}
                max={getMaxDate()}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none cursor-pointer"
                title="Select date from calendar only (future dates not allowed)"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Weight Slip Ref No</label>
              <input
                type="text"
                name="weight_slip_ref_no"
                value={formData.weight_slip_ref_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Vehicle No</label>
              <input
                type="text"
                name="vehicle_no"
                value={formData.vehicle_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Lifted Quantity (MT)</label>
              <input
                type="number"
                name="lifted_quantity_mt"
                value={formData.lifted_quantity_mt}
                onChange={handleChange}
                step="0.001"
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">CMA</label>
              <SearchableDropdown
                options={cmas}
                value={formData.cha_cma_id}
                onChange={val => handleDropdownChange('cha_cma_id', val)}
                placeholder="Select CMA"
                displayKey="name"
                valueKey="cha_cma_id"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Warehouse</label>
              <SearchableDropdown
                options={warehouses.map(w => ({ ...w, name: `${w.name} (${w.location})` }))}
                value={formData.warehouse_id}
                onChange={val => handleDropdownChange('warehouse_id', val)}
                placeholder="Select Warehouse"
                displayKey="name"
                valueKey="warehouse_id"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Commodity</label>
              <SearchableDropdown
                options={commodities}
                value={formData.commodity_id}
                onChange={val => handleDropdownChange('commodity_id', val)}
                placeholder="Select Commodity"
                displayKey="name"
                valueKey="commodity_id"
                required
                customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Dispatch Amount (INR)</label>
              <input
                type="number"
                name="dispatch_amount"
                value={formData.dispatch_amount}
                onChange={handleChange}
                step="0.01"
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Invoice No</label>
              <input
                type="text"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Invoice Date</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Invoice Amount (INR)</label>
              <input
                type="number"
                name="invoice_amount"
                value={formData.invoice_amount}
                onChange={handleChange}
                step="0.01"
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#3899cf] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            style={{ minWidth: '100px' }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Dispatch'}
          </button>
          {message && (
            <div className={`mt-2 text-center text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default DispatchEntryForm;